/**
 * Per-session, per-host tunnel state machine.
 *
 * Each `(sessionId, hostId)` pair owns an independent {@link TunnelRecord}
 * with explicit lifecycle states — `connecting | healthy | degraded | dead`
 * — a periodic heartbeat, automatic reconnect with exponential backoff, and
 * an event emitter for the inspector. Tunnels are intentionally
 * UI-side-only in this unit (the backend `src/infra/ssh-tunnel.ts` is the
 * actual transport, but the lifecycle the operator sees and the place
 * where "tunnel died" becomes actionable lives here in the UI).
 *
 * Design contract (matches the active task's acceptance criteria):
 *
 * 1. **Isolated.** One record per (sessionId, hostId) — switching sessions
 *    or hosts never crosses state.
 * 2. **Inspector-only.** {@link TunnelRecord.state} is the canonical
 *    signal. The composer MUST NOT receive tunnel text in any state; the
 *    `onStateChange` callback receives the record and is the only place
 *    the UI may surface it.
 * 3. **No silent stops.** `dead` is sticky until `reconnect()` is called.
 *    The caller is responsible for surfacing a reconnect affordance; this
 *    module does not auto-resurrect `dead` tunnels (operator consent).
 * 4. **Backoff is bounded.** Reconnect attempts after a `healthy` →
 *    `connecting` round-trip follow the same schedule as fresh starts so
 *    a flapping link never multiplies timers.
 * 5. **Heartbeat is optional but observable.** If the host doesn't
 *    provide heartbeat samples, the record sits in `healthy` and is
 *    still selectable in the inspector; the inspector renders a
 *    "heartbeat: stale" hint after a configurable threshold
 *    ({@link TUNNEL_HEARTBEAT_STALE_MS}).
 *
 * This module has zero React/Lit dependencies — it is a pure state
 * machine that the UI binds to.
 */

export type TunnelState = "connecting" | "healthy" | "degraded" | "dead";

/** Heartbeat considered fresh below this many ms. */
export const TUNNEL_HEARTBEAT_STALE_MS = 30_000;

/** After this many ms without a `healthy` sample, `healthy` → `degraded`. */
export const TUNNEL_HEALTHY_TO_DEGRADED_MS = 60_000;

/** After this many ms without a `healthy` sample, `degraded` → `dead`. */
export const TUNNEL_DEGRADED_TO_DEAD_MS = 120_000;

/** Reconnect backoff schedule (ms). Last value is reused on further retries. */
export const TUNNEL_RECONNECT_BACKOFF_MS: readonly number[] = [
  1_000, 2_000, 4_000, 8_000, 15_000, 30_000,
];

/** Maximum number of reconnect attempts before leaving the tunnel in `dead`. */
export const TUNNEL_MAX_AUTO_RECONNECTS = TUNNEL_RECONNECT_BACKOFF_MS.length;

export type TunnelKey = {
  sessionId: string;
  hostId: string;
};

export type TunnelRecord = {
  key: TunnelKey;
  state: TunnelState;
  /** Last wall-clock ms of a successful heartbeat (healthy sample). */
  lastHealthyAt: number | null;
  /** Last wall-clock ms of a state transition. */
  lastTransitionAt: number;
  /** Optional error from the most recent failure (host unavailable, etc.). */
  lastError: string | null;
  /** How many times we have auto-reconnected without operator intervention. */
  autoReconnectAttempts: number;
  /** Whether `dead` is sticky (i.e. an operator reconnect is required). */
  deadSticky: boolean;
};

export type TunnelStateListener = (record: TunnelRecord) => void;

type InternalRecord = TunnelRecord & {
  listeners: Set<TunnelStateListener>;
};

export type TunnelManagerOptions = {
  now?: () => number;
  /** Override the "stale" threshold for tests. */
  healthyToDegradedMs?: number;
  degradedToDeadMs?: number;
};

/**
 * Registry of tunnel records keyed by `${sessionId}::${hostId}`.
 *
 * Created once per UI shell (in `OpenClawApp`'s constructor in app.ts)
 * and bound to its lifecycle. The manager does NOT own a heartbeat
 * timer — callers (e.g. the `tunnel-health` controller) drive the clock
 * by calling {@link TunnelManager.tick} on a fixed interval. This keeps
 * the machine testable without timers.
 */
export class TunnelManager {
  private readonly records = new Map<string, InternalRecord>();

  private readonly now: () => number;

  private readonly healthyToDegradedMs: number;

  private readonly degradedToDeadMs: number;

  /**
   * Listeners attached via `subscribeAll` that fire on every record
   * mutation, across all (sessionId, hostId) pairs. The list is also
   * attached to records that are created AFTER the subscription so
   * future tunnels also notify the global listener. The
   * per-key `subscribe` listeners are kept on the record itself
   * because they're keyed; `subscribeAll` is broadcast.
   */
  private readonly globalListeners: Set<TunnelStateListener> = new Set();

  constructor(opts: TunnelManagerOptions = {}) {
    this.now = opts.now ?? Date.now;
    this.healthyToDegradedMs = opts.healthyToDegradedMs ?? TUNNEL_HEALTHY_TO_DEGRADED_MS;
    this.degradedToDeadMs = opts.degradedToDeadMs ?? TUNNEL_DEGRADED_TO_DEAD_MS;
  }

  /** Build the composite map key. Exported for tests. */
  static keyOf(key: TunnelKey): string {
    return `${key.sessionId}::${key.hostId}`;
  }

  /**
   * Get the current record, or `null` if no tunnel has been registered
   * for this (sessionId, hostId) yet. The inspector uses this to decide
   * whether to render an empty state or a chip.
   */
  get(key: TunnelKey): TunnelRecord | null {
    const rec = this.records.get(TunnelManager.keyOf(key));
    if (!rec) {
      return null;
    }
    return this.clone(rec);
  }

  /**
   * Subscribe to state changes for a single (sessionId, hostId). Returns
   * an unsubscribe function. The listener fires AFTER the record has
   * been updated, with a fresh clone.
   */
  subscribe(key: TunnelKey, listener: TunnelStateListener): () => void {
    const rec = this.ensureRecord(key);
    rec.listeners.add(listener);
    // Emit current state synchronously so subscribers can paint without
    // an extra round trip.
    listener(this.clone(rec));
    return () => {
      rec.listeners.delete(listener);
    };
  }

  /**
   * Subscribe to ALL records. Fires once per record mutation, across all
   * sessions/hosts — including records that are created after the
   * subscription. The inspector uses this to repaint the "Hosts" tab
   * whenever any tunnel changes state.
   */
  subscribeAll(listener: TunnelStateListener): () => void {
    this.globalListeners.add(listener);
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  /** Snapshot of every record, sorted by (sessionId, hostId). */
  list(): TunnelRecord[] {
    return [...this.records.values()]
      .map((rec) => this.clone(rec))
      .toSorted((a, b) => {
        if (a.key.sessionId === b.key.sessionId) {
          return a.key.hostId.localeCompare(b.key.hostId);
        }
        return a.key.sessionId.localeCompare(b.key.sessionId);
      });
  }

  /**
   * Mark a tunnel as freshly connecting. Called by the WS client
   * `open` handler in `gateway.ts`. Idempotent: re-entering `connecting`
   * from any state (except `dead`, which is sticky) clears the error
   * and resets the auto-reconnect counter.
   */
  markConnecting(key: TunnelKey): TunnelRecord {
    const rec = this.ensureRecord(key);
    if (rec.state === "dead" && rec.deadSticky) {
      // Dead is sticky. Operator must call `requestReconnect`.
      return this.clone(rec);
    }
    rec.state = "connecting";
    rec.lastError = null;
    rec.lastTransitionAt = this.now();
    // We don't reset autoReconnectAttempts here — that counter is for
    // transitions FROM dead/healthy in a single lifecycle. Fresh starts
    // are tracked by the caller.
    this.emit(rec);
    return this.clone(rec);
  }

  /**
   * Mark a tunnel healthy (TCP+WS up AND first heartbeat sample received).
   * Called by the WS client's `onopen` AND by the heartbeat tick after
   * the first `tick` round-trip.
   */
  markHealthy(key: TunnelKey): TunnelRecord {
    const rec = this.ensureRecord(key);
    if (rec.state === "dead" && rec.deadSticky) {
      return this.clone(rec);
    }
    rec.state = "healthy";
    rec.lastHealthyAt = this.now();
    rec.lastTransitionAt = this.now();
    rec.lastError = null;
    rec.autoReconnectAttempts = 0;
    this.emit(rec);
    return this.clone(rec);
  }

  /**
   * Mark a tunnel as temporarily degraded (WS still up but heartbeat
   * stale, or non-fatal protocol error). The manager will only flip
   * the state to `degraded` if the previous state was `healthy`; other
   * transitions (e.g. `connecting` → `degraded`) are not valid because
   * the tunnel was never healthy to begin with.
   */
  markDegraded(key: TunnelKey, reason?: string): TunnelRecord {
    const rec = this.ensureRecord(key);
    if (rec.state === "dead" && rec.deadSticky) {
      return this.clone(rec);
    }
    if (rec.state !== "healthy") {
      // Don't bounce into `degraded` from `connecting` — that's a hard
      // failure and should call markDead directly. Keep the current
      // state.
      return this.clone(rec);
    }
    rec.state = "degraded";
    rec.lastTransitionAt = this.now();
    rec.lastError = reason ?? null;
    this.emit(rec);
    return this.clone(rec);
  }

  /**
   * Mark a tunnel as dead. The tunnel stops auto-reconnecting; the
   * operator must explicitly call {@link requestReconnect}. Failure
   * events from the WS client (e.g. close code != 1000) call this.
   */
  markDead(key: TunnelKey, reason?: string): TunnelRecord {
    const rec = this.ensureRecord(key);
    rec.state = "dead";
    rec.lastTransitionAt = this.now();
    rec.lastError = reason ?? null;
    rec.deadSticky = true;
    this.emit(rec);
    return this.clone(rec);
  }

  /**
   * Drop the sticky `dead` flag and start a fresh connect cycle.
   * Returns the next backoff delay (ms) so the caller can schedule the
   * reconnect timer without duplicating the schedule table.
   */
  requestReconnect(key: TunnelKey): { record: TunnelRecord; delayMs: number } {
    const rec = this.ensureRecord(key);
    rec.deadSticky = false;
    rec.state = "connecting";
    rec.lastError = null;
    rec.lastTransitionAt = this.now();
    rec.autoReconnectAttempts = 0;
    this.emit(rec);
    return {
      record: this.clone(rec),
      delayMs: TUNNEL_RECONNECT_BACKOFF_MS[0] ?? 1_000,
    };
  }

  /**
   * Auto-reconnect bookkeeping. Called by the tunnel-health controller
   * after a `dead` is auto-recovered (i.e. the WS came back without the
   * operator pressing reconnect). Returns `null` when the auto-reconnect
   * budget is exhausted — the caller must then call {@link markDead} and
   * stop trying.
   */
  consumeAutoReconnect(key: TunnelKey): { delayMs: number; exhausted: boolean } {
    const rec = this.ensureRecord(key);
    const idx = Math.min(rec.autoReconnectAttempts, TUNNEL_RECONNECT_BACKOFF_MS.length - 1);
    rec.autoReconnectAttempts += 1;
    const delayMs = TUNNEL_RECONNECT_BACKOFF_MS[idx] ?? 30_000;
    const exhausted = rec.autoReconnectAttempts > TUNNEL_MAX_AUTO_RECONNECTS;
    return { delayMs, exhausted };
  }

  /**
   * Advance the heartbeat clock. The manager decides whether any record
   * has aged out of `healthy` or `degraded` and emits the resulting
   * state change. Intended to be called by the tunnel-health
   * controller on a fixed interval (e.g. every 5s).
   */
  tick(): TunnelRecord[] {
    const now = this.now();
    const changed: TunnelRecord[] = [];
    for (const rec of this.records.values()) {
      if (rec.deadSticky) {
        continue;
      }
      const age = rec.lastHealthyAt == null ? Infinity : now - rec.lastHealthyAt;
      if (rec.state === "healthy" && age > this.healthyToDegradedMs) {
        rec.state = "degraded";
        rec.lastTransitionAt = now;
        rec.lastError = "heartbeat stale";
        this.emit(rec);
        changed.push(this.clone(rec));
        continue;
      }
      if (rec.state === "degraded" && age > this.degradedToDeadMs) {
        rec.state = "dead";
        rec.lastTransitionAt = now;
        rec.lastError = "heartbeat missing";
        rec.deadSticky = true;
        this.emit(rec);
        changed.push(this.clone(rec));
        continue;
      }
    }
    return changed;
  }

  /**
   * Heartbeat sample — call when the WS client receives a server `tick`
   * response or any other live event. Resets the `lastHealthyAt` clock
   * for the given (sessionId, hostId) and promotes `degraded`/`connecting`
   * back to `healthy`. Does NOT promote `dead` (operator consent).
   */
  heartbeat(key: TunnelKey): TunnelRecord {
    const rec = this.ensureRecord(key);
    if (rec.state === "dead" && rec.deadSticky) {
      return this.clone(rec);
    }
    rec.lastHealthyAt = this.now();
    if (rec.state === "degraded" || rec.state === "connecting") {
      rec.state = "healthy";
      rec.lastTransitionAt = this.now();
      rec.lastError = null;
      rec.autoReconnectAttempts = 0;
      this.emit(rec);
    }
    return this.clone(rec);
  }

  /**
   * Forget a tunnel entirely. The inspector uses this when a session is
   * closed so the record doesn't accumulate.
   */
  forget(key: TunnelKey): void {
    this.records.delete(TunnelManager.keyOf(key));
  }

  private ensureRecord(key: TunnelKey): InternalRecord {
    const k = TunnelManager.keyOf(key);
    let rec = this.records.get(k);
    if (!rec) {
      rec = {
        key: { sessionId: key.sessionId, hostId: key.hostId },
        state: "connecting",
        lastHealthyAt: null,
        lastTransitionAt: this.now(),
        lastError: null,
        autoReconnectAttempts: 0,
        deadSticky: false,
        listeners: new Set(),
      };
      this.records.set(k, rec);
    }
    return rec;
  }

  private emit(rec: InternalRecord): void {
    const snapshot = this.clone(rec);
    for (const listener of rec.listeners) {
      listener(snapshot);
    }
    for (const listener of this.globalListeners) {
      listener(snapshot);
    }
  }

  private clone(rec: InternalRecord): TunnelRecord {
    return {
      key: { sessionId: rec.key.sessionId, hostId: rec.key.hostId },
      state: rec.state,
      lastHealthyAt: rec.lastHealthyAt,
      lastTransitionAt: rec.lastTransitionAt,
      lastError: rec.lastError,
      autoReconnectAttempts: rec.autoReconnectAttempts,
      deadSticky: rec.deadSticky,
    };
  }
}

/**
 * Best-effort hostId derivation for the current UI shell.
 *
 * The active task assumes a future multi-host world where each session
 * can live on a different remote host. The hostId is the WebSocket URL
 * origin today (one host per UI), and the resolution is intentionally
 * trivial so adding real multi-host routing later does not change the
 * call site.
 */
export function hostIdForGatewayUrl(gatewayUrl: string): string {
  try {
    const u = new URL(gatewayUrl);
    return `${u.protocol}//${u.host}`;
  } catch {
    return "unknown";
  }
}
